// The app's single outline icon set (foundations.md §5: one outline family per
// product, 24 px grid, 2 px stroke — the same family the in-call control bar has
// always used). Every screen imports icons from here; no second family, no emoji.
import type { ReactNode } from 'react'

type IconProps = {
  className?: string
}

const outline = (children: ReactNode) =>
  function OutlineIcon({ className }: IconProps) {
    return (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        className={className}
      >
        {children}
      </svg>
    )
  }

export const PhoneIcon = outline(
  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
)

export const PhoneOffIcon = outline(
  <>
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91" />
    <line x1="23" y1="1" x2="1" y2="23" />
  </>
)

export const MicIcon = outline(
  <>
    <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
    <path d="M19 11v1a7 7 0 0 1-14 0v-1" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </>
)

export const MicOffIcon = outline(
  <>
    <line x1="3" y1="3" x2="21" y2="21" />
    <path d="M9 9v2a3 3 0 0 0 4.83 2.39" />
    <path d="M15 11.5V5a3 3 0 0 0-5.92-.7" />
    <path d="M19 11v1a7 7 0 0 1-10.5 6.06M5 11v1a7 7 0 0 0 .2 1.66" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </>
)

export const CameraIcon = outline(
  <>
    <rect x="2" y="6" width="13" height="12" rx="2" />
    <path d="M22 8.5 15 12l7 3.5v-7z" />
  </>
)

export const CameraOffIcon = outline(
  <>
    <line x1="3" y1="3" x2="21" y2="21" />
    <path d="M15 11.5V8a2 2 0 0 0-2-2H7M4.2 6.2A2 2 0 0 0 2 8v8a2 2 0 0 0 2 2h9a2 2 0 0 0 1.8-1.1" />
    <path d="M22 8.5 15 12v0l7 3.5v-7z" />
  </>
)

export const FlipCameraIcon = outline(
  <>
    <polyline points="21 4 21 9 16 9" />
    <polyline points="3 20 3 15 8 15" />
    <path d="M19.5 9A7.5 7.5 0 0 0 6.8 5.5L3 9m18 6-3.8 3.5A7.5 7.5 0 0 1 4.5 15" />
  </>
)

/** Filled, rotated handset — the universal red hang-up glyph. */
export function HangUpIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true" focusable="false" className={className}>
      <g transform="rotate(135 12 12)">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
      </g>
    </svg>
  )
}

export const ClockIcon = outline(
  <>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </>
)

export const AlertIcon = outline(
  <>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </>
)

export const CheckIcon = outline(<polyline points="20 6 9 17 4 12" />)

export const XIcon = outline(
  <>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </>
)

export const OfflineIcon = outline(
  <>
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
    <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </>
)

export const PencilIcon = outline(
  <>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </>
)

export const LinkIcon = outline(
  <>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </>
)

type StatusTone = 'success' | 'danger' | 'warning' | 'primary'

/** Status-screen icon plate (web/product.md §22): icon inside a status-tinted circle. */
export function StatusIcon({ tone, icon: Icon }: { tone: StatusTone; icon: (p: IconProps) => ReactNode }) {
  return (
    <div className={`cf-status-icon ${tone}`}>
      <Icon />
    </div>
  )
}
